import { useId, useState } from 'react';

export type FileToDataURLProps = {
  label: string;
  accept?: string;
  maxBytes?: number;
  value?: string;
  onChange: (dataUrl: string) => void;
};

const DEFAULT_MAX = 1 << 20; // 1 MiB to match server cap.

/**
 * File-input → base64 data URL. Reads the chosen file as a data URL
 * via FileReader, validates the size cap up front, and emits the
 * encoded string via `onChange`. The current value renders as a small
 * preview when present (so an existing logo round-trips through the
 * form without a re-upload).
 */
export function FileToDataURL({
  label,
  accept = 'image/png,image/jpeg,image/svg+xml',
  maxBytes = DEFAULT_MAX,
  value,
  onChange,
}: FileToDataURLProps) {
  const inputId = useId();
  const [err, setErr] = useState<string | null>(null);

  const handle = (file: File) => {
    setErr(null);
    if (file.size > maxBytes) {
      setErr(`File exceeds ${Math.round(maxBytes / 1024)} KB`);
      return;
    }
    const reader = new FileReader();
    reader.onerror = () => setErr('Couldn’t read the file.');
    reader.onload = () => {
      if (typeof reader.result === 'string') onChange(reader.result);
    };
    reader.readAsDataURL(file);
  };

  return (
    <div className="flex flex-col gap-sp-2">
      <label
        htmlFor={inputId}
        className="text-fs-eyebrow font-medium uppercase tracking-[0.14em] text-gray-700"
      >
        {label}
      </label>
      <div className="flex items-center gap-sp-3">
        {value ? (
          <img
            src={value}
            alt=""
            className="h-12 w-12 rounded-r-default border border-gray-300 bg-gray-50 object-contain"
          />
        ) : null}
        <input
          id={inputId}
          type="file"
          accept={accept}
          className="text-fs-meta text-gray-900"
          onChange={(e) => {
            const f = e.target.files?.[0];
            if (f) handle(f);
            e.currentTarget.value = '';
          }}
        />
        {value ? (
          <button
            type="button"
            onClick={() => onChange('')}
            className="text-fs-meta text-info hover:underline"
          >
            Remove
          </button>
        ) : null}
      </div>
      {err ? (
        <p role="alert" className="text-fs-meta text-critical">
          {err}
        </p>
      ) : null}
    </div>
  );
}
